import { useEffect, useRef } from 'react';
import TipsIcon from 'components/TipsIcon';
import { useSelector, useDispatch } from 'react-redux';
import { GET_DOWNLOAD_DOCUMENT } from 'store/key';
import { getTimeWithTimeZone } from 'utility/general';
import { SET_DOCUMENT_UPLOAD } from 'store/key';
import CustomLink from 'components/CustomLink';
import usePrevious from 'hooks/usePrevious';
import { isEqual } from 'lodash';

const useDocumentUploadData = (props) => {
    const dispatch = useDispatch();

    const { tabType, formId, siteInfoId, isStudyInfo } = props;
    const onlyShowInStudyInfo = tabType === 'info';
    const onlyShowInSiteInfo = !onlyShowInStudyInfo;

    const userInfo = useSelector((state) => state.user.detail);
    const documentUpload = useSelector((state) => state.documentUpload);

    const userName = `${userInfo.firstName} ${userInfo.surname}`;
    const {
        researchProtocolRows,
        insuranceCertificateRows,
        informedConsentRows,
        brochureRows,
        writtenRows,
        questionnairesRows,
        declarationRows,
        indemnityAgreementRows,
        otherDocumentsRows,
        supportingDocumentRows,
        decisionDocumentRows,
        otherFundingSourceRows,
        signedApplicationFormRows,
    } = documentUpload;

    const remoteDataCti = useSelector((state) => state.applicationDetail?.[formId]?.cti);
    const previousDataCti = usePrevious(remoteDataCti);
    const remoteDataUploadDocuments = useSelector((state) => state.applicationDetail?.[formId]?.uploadDocuments);
    const previousDataUploadDocuments = usePrevious(remoteDataUploadDocuments);

    const getFileNameContent = (params, type) => {
        let fileName = params.getValue('fileName');
        return params.row.existInDB ? (
            <CustomLink isStopJumping onClick={() => getDownloadDocument(params.row, type)}>
                {fileName}
            </CustomLink>
        ) : (
            fileName
        );
    };

    const researchProtocolDefaultFormList = {
        createdBy: {
            value: userName,
            label: 'Created By',
            name: 'createdBy',
        },
        fileName: {
            value: [],
            label: 'File Name',
            name: 'fileName',
            type: 'uploadFile',
            multiple: true,
            validators: ['isRequired'],
            enableRemove: true,
        },
    };
    const researchProtocolColumns = [
        {
            field: 'updateDate',
            headerName: 'Upload Date',
            width: 150,
            valueGetter: (params) => {
                let date = params.getValue('updateDate');
                date = date ? getTimeWithTimeZone(date).format('YYYY-MM-DD') : '-';
                return date;
            },
        },
        { field: 'createdByFullName', headerName: 'Created By', width: 150 },
        {
            field: 'fileName',
            headerName: 'File Name',
            width: 400,
            valueGetter: (params) => getFileNameContent(params),
        },
    ];

    const insuranceCertificateDefaultFormList = {
        createdByFullName: {
            value: userName,
            label: 'Created By',
            name: 'createdByFullName',
        },
        expiryDtm: {
            value: null,
            label: 'Policy Expiry Date',
            name: 'expiryDtm',
            type: 'date',
            showRed: false,
            validators: ['isRequired'],
        },
        policyNo: {
            value: '',
            label: 'Policy Number',
            name: 'policyNo',
            type: 'input',
            validators: ['isRequired', 'disableChinese'],
            maxLength: 255,
        },
        fileName: {
            value: [],
            label: 'File Name',
            name: 'fileName',
            type: 'uploadFile',
            multiple: true,
            validators: ['isRequired'],
            enableRemove: true,
            showFileSize: 1,
            alignItems: 'center',
            showRed: false
        },
    };
    const insuranceCertificateColumns = [
        {
            field: 'updateDate',
            headerName: 'Upload Date',
            width: 200,
            valueGetter: (params) => {
                let date = params.getValue('updateDate');
                date = date ? getTimeWithTimeZone(date).format('YYYY-MM-DD') : '-';
                return date;
            },
        },
        { field: 'createdByFullName', headerName: 'Created By', width: 200 },
        {
            field: 'expiryDtm',
            headerName: 'Policy Expiry Date',
            width: 200,
            valueGetter: (params) => {
                let date = params.getValue('expiryDtm');
                date = date ? getTimeWithTimeZone(date).format('YYYY-MM-DD') : '-';
                return date;
            },
        },
        { field: 'policyNo', headerName: 'Policy Number', width: 200 },
        {
            field: 'fileName',
            headerName: 'File Name',
            width: 300,
            valueGetter: (params) => getFileNameContent(params, 'cti'),
        },
    ];

    const informedConsentDefaultFormList = {
        createdByFullName: {
            value: userName,
            label: 'Created By',
            name: 'createdByFullName',
        },
        fileName: {
            value: [],
            label: 'File Name',
            name: 'fileName',
            type: 'uploadFile',
            multiple: true,
            validators: ['isRequired'],
            enableRemove: true,
        },
    };
    const informedConsentColumns = [
        {
            field: 'updateDate',
            headerName: 'Upload Date',
            width: 150,
            valueGetter: (params) => {
                let date = params.getValue('updateDate');
                date = date ? getTimeWithTimeZone(date).format('YYYY-MM-DD') : '-';
                return date;
            },
        },
        { field: 'createdByFullName', headerName: 'Created By', width: 150 },
        {
            field: 'fileName',
            headerName: 'File Name',
            width: 400,
            valueGetter: (params) => getFileNameContent(params),
        },
    ];

    const brochureDefaultFormList = {
        createdByFullName: {
            value: userName,
            label: 'Created By',
            name: 'createdByFullName',
        },
        fileName: {
            value: [],
            label: 'File Name',
            name: 'fileName',
            type: 'uploadFile',
            multiple: true,
            validators: ['isRequired'],
            enableRemove: true,
        },
    };
    const brochureColumns = [
        {
            field: 'updateDate',
            headerName: 'Upload Date',
            width: 150,
            valueGetter: (params) => {
                let date = params.getValue('updateDate');
                date = date ? getTimeWithTimeZone(date).format('YYYY-MM-DD') : '-';
                return date;
            },
        },
        { field: 'createdByFullName', headerName: 'Created By', width: 150 },
        {
            field: 'fileName',
            headerName: 'File Name',
            width: 400,
            valueGetter: (params) => getFileNameContent(params),
        },
    ];

    const writtenDefaultFormList = {
        createdByFullName: {
            value: userName,
            label: 'Created By',
            name: 'createdByFullName',
        },
        fileName: {
            value: [],
            label: 'File Name',
            name: 'fileName',
            type: 'uploadFile',
            multiple: true,
            validators: ['isRequired'],
            enableRemove: true,
        },
    };
    const writtenColumns = [
        {
            field: 'updateDate',
            headerName: 'Upload Date',
            width: 150,
            valueGetter: (params) => {
                let date = params.getValue('updateDate');
                date = date ? getTimeWithTimeZone(date).format('YYYY-MM-DD') : '-';
                return date;
            },
        },
        { field: 'createdByFullName', headerName: 'Created By', width: 150 },
        {
            field: 'fileName',
            headerName: 'File Name',
            width: 400,
            valueGetter: (params) => getFileNameContent(params),
        },
    ];

    const questionnairesDefaultFormList = {
        createdByFullName: {
            value: userName,
            label: 'Created By',
            name: 'createdByFullName',
        },
        fileName: {
            value: [],
            label: 'File Name',
            name: 'fileName',
            type: 'uploadFile',
            multiple: true,
            validators: ['isRequired'],
            enableRemove: true,
        },
    };
    const questionnairesColumns = [
        {
            field: 'updateDate',
            headerName: 'Upload Date',
            width: 150,
            valueGetter: (params) => {
                let date = params.getValue('updateDate');
                date = date ? getTimeWithTimeZone(date).format('YYYY-MM-DD') : '-';
                return date;
            },
        },
        { field: 'createdByFullName', headerName: 'Created By', width: 150 },
        {
            field: 'fileName',
            headerName: 'File Name',
            width: 400,
            valueGetter: (params) => getFileNameContent(params),
        },
    ];

    const declarationDefaultFormList = {
        createdByFullName: {
            value: userName,
            label: 'Created By',
            name: 'createdByFullName',
        },
        fileName: {
            value: [],
            label: 'File Name',
            name: 'fileName',
            type: 'uploadFile',
            multiple: true,
            validators: ['isRequired'],
            enableRemove: true,
            showRed: false
        },
    };
    const declarationColumns = [
        {
            field: 'updateDate',
            headerName: 'Upload Date',
            width: 150,
            valueGetter: (params) => {
                let date = params.getValue('updateDate');
                date = date ? getTimeWithTimeZone(date).format('YYYY-MM-DD') : '-';
                return date;
            },
        },
        { field: 'createdByFullName', headerName: 'Created By', width: 150 },
        {
            field: 'fileName',
            headerName: 'File Name',
            width: 400,
            valueGetter: (params) => getFileNameContent(params),
        },
    ];

    const indemnityAgreementDefaultFormList = {
        createdByFullName: {
            value: userName,
            label: 'Created By',
            name: 'createdByFullName',
        },
        fileName: {
            value: [],
            label: 'File Name',
            name: 'fileName',
            type: 'uploadFile',
            multiple: true,
            validators: ['isRequired'],
            enableRemove: true,
            showRed: false
        },
    };
    const indemnityAgreementColumns = [
        {
            field: 'updateDate',
            headerName: 'Upload Date',
            width: 150,
            valueGetter: (params) => {
                let date = params.getValue('updateDate');
                date = date ? getTimeWithTimeZone(date).format('YYYY-MM-DD') : '-';
                return date;
            },
        },
        { field: 'createdByFullName', headerName: 'Created By', width: 150 },
        {
            field: 'fileName',
            headerName: 'File Name',
            width: 400,
            valueGetter: (params) => getFileNameContent(params),
        },
    ];

    const otherDocumentsDefaultFormList = {
        createdByFullName: {
            value: userName,
            label: 'Created By',
            name: 'createdByFullName',
        },
        fileName: {
            value: [],
            label: 'File Name',
            name: 'fileName',
            type: 'uploadFile',
            multiple: true,
            validators: ['isRequired'],
            enableRemove: true,
        },
    };
    const otherDocumentsColumns = [
        {
            field: 'updateDate',
            headerName: 'Upload Date',
            width: 150,
            valueGetter: (params) => {
                let date = params.getValue('updateDate');
                date = date ? getTimeWithTimeZone(date).format('YYYY-MM-DD') : '-';
                return date;
            },
        },
        { field: 'createdByFullName', headerName: 'Created By', width: 150 },
        {
            field: 'fileName',
            headerName: 'File Name',
            width: 400,
            valueGetter: (params) => getFileNameContent(params),
        },
    ];

    const supportingDocumentDefaultFormList = {
        createdByFullName: {
            value: userName,
            label: 'Created By',
            name: 'createdByFullName',
        },
        fileName: {
            value: [],
            label: 'File Name',
            name: 'fileName',
            type: 'uploadFile',
            multiple: true,
            validators: ['isRequired'],
            enableRemove: true,
            showRed: false
        },
    };
    const supportingDocumentColumns = [
        {
            field: 'updateDate',
            headerName: 'Upload Date',
            width: 150,
            valueGetter: (params) => {
                let date = params.getValue('updateDate');
                date = date ? getTimeWithTimeZone(date).format('YYYY-MM-DD') : '-';
                return date;
            },
        },
        { field: 'createdByFullName', headerName: 'Created By', width: 150 },
        {
            field: 'fileName',
            headerName: 'File Name',
            width: 400,
            valueGetter: (params) => getFileNameContent(params),
        },
    ];

    const decisionDocumentDefaultFormList = {
        createdByFullName: {
            value: userName,
            label: 'Created By',
            name: 'createdByFullName',
        },
        fileName: {
            value: [],
            label: 'File Name',
            name: 'fileName',
            type: 'uploadFile',
            multiple: true,
            validators: ['isRequired'],
            enableRemove: true,
            showRed: false
        },
    };
    const decisionDocumentColumns = [
        {
            field: 'updateDate',
            headerName: 'Upload Date',
            width: 150,
            valueGetter: (params) => {
                let date = params.getValue('updateDate');
                date = date ? getTimeWithTimeZone(date).format('YYYY-MM-DD') : '-';
                return date;
            },
        },
        { field: 'createdByFullName', headerName: 'Created By', width: 150 },
        {
            field: 'fileName',
            headerName: 'File Name',
            width: 400,
            valueGetter: (params) => getFileNameContent(params),
        },
    ];

    const otherFundingSourceDefaultFormList = {
        createdByFullName: {
            value: userName,
            label: 'Created By',
            name: 'createdByFullName',
        },
        fileName: {
            value: [],
            label: 'File Name',
            name: 'fileName',
            type: 'uploadFile',
            multiple: true,
            validators: ['isRequired'],
            enableRemove: true,
            showRed: false
        },
    };
    const otherFundingSourceColumns = [
        {
            field: 'updateDate',
            headerName: 'Upload Date',
            width: 150,
            valueGetter: (params) => {
                let date = params.getValue('updateDate');
                date = date ? getTimeWithTimeZone(date).format('YYYY-MM-DD') : '-';
                return date;
            },
        },
        { field: 'createdByFullName', headerName: 'Created By', width: 150 },
        {
            field: 'fileName',
            headerName: 'File Name',
            width: 400,
            valueGetter: (params) => getFileNameContent(params),
        },
    ];

    const signedApplicationFormDefaultFormList = {
        createdByFullName: {
            value: userName,
            label: 'Created By',
            name: 'createdByFullName',
        },
        fileName: {
            value: [],
            label: 'File Name',
            name: 'fileName',
            type: 'uploadFile',
            multiple: true,
            validators: ['isRequired'],
            enableRemove: true,
            showRed: false
        },
    };
    const signedApplicationFormColumns = [
        {
            field: 'updateDate',
            headerName: 'Upload Date',
            width: 150,
            valueGetter: (params) => {
                let date = params.getValue('updateDate');
                date = date ? getTimeWithTimeZone(date).format('YYYY-MM-DD') : '-';
                return date;
            },
        },
        { field: 'createdByFullName', headerName: 'Created By', width: 150 },
        {
            field: 'fileName',
            headerName: 'File Name',
            width: 400,
            valueGetter: (params) => getFileNameContent(params),
        },
    ];

    const setRows = (key, rows) => {
        let data = null;
        if (!isStudyInfo) {
            data = { id: siteInfoId };
        }
        const newRows = rows.map((item) => {
            if (item.siteInfo === undefined) {
                item.siteInfo = data;
            }
            return item;
        });
        dispatch({
            type: SET_DOCUMENT_UPLOAD,
            payload: {
                [key]: newRows,
            },
        });
    };

    const contentList = [
        {
            type: 0,
            number: '22',
            popupTitle: 'Research Protocol',
            label: 'Research Protocol (Mandatory)',
            show: onlyShowInStudyInfo,
            columns: researchProtocolColumns,
            rows: researchProtocolRows,
            defaultFormList: researchProtocolDefaultFormList,
            setRows: (rows) => setRows('researchProtocolRows', rows),
            displayTable: true,
            indexAddDot: true,
            indexHeaderName: 'Row',
        },
        {
            type: 1,
            number: '23',
            popupTitle: 'Subject Information Sheet and Informed Consent Form',
            label: (
                <>
                    Subject Information Sheet and Informed Consent Form{' '}
                    <TipsIcon title="Should be provided unless waived by CIRB/CREC/IRB" /> (Supplementary)
                </>
            ),
            show: true,
            columns: informedConsentColumns,
            rows: informedConsentRows,
            defaultFormList: informedConsentDefaultFormList,
            setRows: (rows) => setRows('informedConsentRows', rows),
            displayTable: true,
            indexAddDot: true,
            indexHeaderName: 'Row',
        },
        {
            type: 2,
            number: '24',
            popupTitle: "Investigator's Brochure",
            label: "Investigator's Brochure (Supplementary)",
            show: onlyShowInStudyInfo,
            columns: brochureColumns,
            rows: brochureRows,
            defaultFormList: brochureDefaultFormList,
            setRows: (rows) => setRows('brochureRows', rows),
            displayTable: true,
            indexAddDot: true,
            indexHeaderName: 'Row',
        },
        {
            type: 3,
            number: '25',
            popupTitle: 'Written information for subject',
            label: (
                <>
                    Written information for subject, such as recruitment advertisement, etc
                    <TipsIcon title="These documents must be in languages suitable for the target subjects." />{' '}
                    (Supplementary)
                </>
            ),
            show: true,
            columns: writtenColumns,
            rows: writtenRows,
            defaultFormList: writtenDefaultFormList,
            setRows: (rows) => setRows('writtenRows', rows),
            displayTable: true,
            indexAddDot: true,
            indexHeaderName: 'Row',
        },
        {
            type: 4,
            number: '26',
            popupTitle: 'Questionnaires',
            label: (
                <>
                    Questionnaires
                    <TipsIcon title="These documents must be in languages suitable for the target subjects." />{' '}
                    (Supplementary)
                </>
            ),
            show: true,
            columns: questionnairesColumns,
            rows: questionnairesRows,
            defaultFormList: questionnairesDefaultFormList,
            setRows: (rows) => setRows('questionnairesRows', rows),
            displayTable: true,
            indexAddDot: true,
            indexHeaderName: 'Row',
        },
        {
            type: 5,
            number: '27',
            popupTitle: 'Conflict of Interest Declaration by all Investigators',
            label: onlyShowInStudyInfo
                ? 'Conflict of Interest Declaration by all Investigators (Mandatory) (*To Complete in Site info)'
                : 'Conflict of Interest Declaration by all Investigators (Mandatory)',
            labelColor: onlyShowInStudyInfo ? '#5ac49f' : '#000',
            toolTips: "*The related form templates can be downloaded at 'Help' -> 'Common Forms'",
            show: true,
            columns: declarationColumns,
            rows: declarationRows,
            defaultFormList: declarationDefaultFormList,
            setRows: (rows) => setRows('declarationRows', rows),
            displayTable: onlyShowInSiteInfo,
            indexAddDot: true,
            indexHeaderName: 'Row',
        },
        {
            type: 6,
            number: '28',
            popupTitle: 'Indemnity Agreement',
            // label: onlyShowInStudyInfo
            //     ? 'Indemnity Agreement (Mandatory for sponsored trial) (*To Complete in Site info)'
            //     : 'Indemnity Agreement (Mandatory for sponsored trial)',
            label: 'Indemnity Agreement (for office use only)',
            // labelColor: onlyShowInStudyInfo ? '#5ac49f' : '#000',
            labelColor: 'grey',
            show: true,
            columns: indemnityAgreementColumns,
            rows: indemnityAgreementRows,
            defaultFormList: indemnityAgreementDefaultFormList,
            setRows: (rows) => setRows('indemnityAgreementRows', rows),
            displayTable: onlyShowInSiteInfo,
            indexAddDot: true,
            indexHeaderName: 'Row',
        },
        {
            type: 'cti',
            number: '29',
            popupTitle: 'Clinical Trial Insurance Certificate',
            label: 'Clinical Trial Insurance Certificate (Mandatory for sponsored trial)',
            show: onlyShowInStudyInfo,
            columns: insuranceCertificateColumns,
            rows: insuranceCertificateRows,
            defaultFormList: insuranceCertificateDefaultFormList,
            setRows: (rows) => setRows('insuranceCertificateRows', rows),
            displayTable: true,
            indexAddDot: true,
            indexHeaderName: 'Row',
        },
        {
            type: 11,
            number: '30',
            popupTitle: onlyShowInStudyInfo ? 'Other Documents – Study based' : 'Other Documents – Site Information',
            label: onlyShowInStudyInfo
                ? 'Other Documents – Study based (Supplementary)'
                : 'Other Documents – Site Information (Supplementary)',
            show: true,
            columns: otherDocumentsColumns,
            rows: otherDocumentsRows,
            defaultFormList: otherDocumentsDefaultFormList,
            setRows: (rows) => setRows('otherDocumentsRows', rows),
            displayTable: true,
            indexAddDot: true,
            indexHeaderName: 'Row',
        },
        {
            type: 7,
            number: '31',
            popupTitle: 'Supporting document from Academia',
            label: 'Supporting document from Academia (Mandatory for student project)',
            show: onlyShowInStudyInfo,
            columns: supportingDocumentColumns,
            rows: supportingDocumentRows,
            defaultFormList: supportingDocumentDefaultFormList,
            setRows: (rows) => setRows('supportingDocumentRows', rows),
            displayTable: true,
            indexAddDot: true,
            indexHeaderName: 'Row',
        },
        {
            type: 8,
            number: '32',
            popupTitle: 'CIRB/CREC/IRB decision document',
            label: 'CIRB/CREC/IRB decision document of clinical research ethics review for cross-cluster study (Mandatory if applicable)',
            show: onlyShowInStudyInfo,
            columns: decisionDocumentColumns,
            rows: decisionDocumentRows,
            defaultFormList: decisionDocumentDefaultFormList,
            setRows: (rows) => setRows('decisionDocumentRows', rows),
            displayTable: true,
            indexAddDot: true,
            indexHeaderName: 'Row',
        },
        {
            type: 9,
            number: '33',
            popupTitle: 'Other funding source(s)',
            label: 'Other funding source(s) (Supplementary)',
            show: onlyShowInStudyInfo,
            columns: otherFundingSourceColumns,
            rows: otherFundingSourceRows,
            defaultFormList: otherFundingSourceDefaultFormList,
            setRows: (rows) => setRows('otherFundingSourceRows', rows),
            displayTable: true,
            indexAddDot: true,
            indexHeaderName: 'Row',
        },
        {
            type: 10,
            number: '34',
            popupTitle: onlyShowInStudyInfo
                ? 'Signed Application Form – Declaration and Endorsement'
                : 'Signed DECLARATION AND ENDORSEMENT',
            label: onlyShowInStudyInfo
                ? 'Signed Application Form – Declaration and Endorsement (Mandatory) (*To Complete in Site info)'
                : 'Signed DECLARATION AND ENDORSEMENT (Mandatory)',
            labelColor: onlyShowInStudyInfo ? '#5ac49f' : '#000',
            show: true,
            columns: signedApplicationFormColumns,
            rows: signedApplicationFormRows,
            defaultFormList: signedApplicationFormDefaultFormList,
            setRows: (rows) => setRows('signedApplicationFormRows', rows),
            displayTable: onlyShowInSiteInfo,
            indexAddDot: true,
            indexHeaderName: 'Row',
        },
    ];

    const initRows = useRef({
        0: {
            type: 0,
            defaultRows: [],
            setRows: (rows) => setRows('researchProtocolRows', rows),
        },
        1: {
            type: 1,
            defaultRows: [],
            setRows: (rows) => setRows('informedConsentRows', rows),
        },
        2: {
            type: 2,
            defaultRows: [],
            setRows: (rows) => setRows('brochureRows', rows),
        },
        3: {
            type: 3,
            defaultRows: [],
            setRows: (rows) => setRows('writtenRows', rows),
        },
        4: {
            type: 4,
            defaultRows: [],
            setRows: (rows) => setRows('questionnairesRows', rows),
        },
        5: {
            type: 5,
            defaultRows: [],
            setRows: (rows) => setRows('declarationRows', rows),
        },
        6: {
            type: 6,
            defaultRows: [],
            setRows: (rows) => setRows('indemnityAgreementRows', rows),
        },
        cti: {
            type: 'cti',
            defaultRows: [],
            setRows: (rows) => setRows('insuranceCertificateRows', rows),
        },
        11: {
            type: 11,
            defaultRows: [],
            setRows: (rows) => setRows('otherDocumentsRows', rows),
        },
        7: {
            type: 7,
            defaultRows: [],
            setRows: (rows) => setRows('supportingDocumentRows', rows),
        },
        8: {
            type: 8,
            defaultRows: [],
            setRows: (rows) => setRows('decisionDocumentRows', rows),
        },
        9: {
            type: 9,
            defaultRows: [],
            setRows: (rows) => setRows('otherFundingSourceRows', rows),
        },
        10: {
            type: 10,
            defaultRows: [],
            setRows: (rows) => setRows('signedApplicationFormRows', rows),
        },
    });

    const refleshData = () => {
        /** clear old data */
        for (let i in initRows.current) {
            initRows.current[i].defaultRows = [];
        }
        /** set new data to defaultRows for uploadDocuments */
        for (let i of remoteDataUploadDocuments) {
            initRows.current[i.type].defaultRows.push({
                ...i,
                existInDB: true,
            });
        }
        /** set new data to defaultRows for cti */
        for (let i of remoteDataCti) {
            initRows.current.cti.defaultRows.push({
                ...i,
                existInDB: true,
                type: 'cti',
            });
        }
        /** set rows */
        const list = JSON.parse(JSON.stringify(initRows.current));
        for (let i in list) {
            const item = list[i];
            if (item.defaultRows.length) {
                initRows.current[item.type].setRows(item.defaultRows);
            } else {
                initRows.current[item.type].setRows([]);
            }
        }
    };

    // update row when remote data update
    useEffect(() => {
        if (
            !isEqual(previousDataCti, remoteDataCti) ||
            !isEqual(previousDataUploadDocuments, remoteDataUploadDocuments)
        ) {
            refleshData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remoteDataCti, remoteDataUploadDocuments]);

    const getDownloadDocument = (row, type) => {
        dispatch({
            type: GET_DOWNLOAD_DOCUMENT,
            payload: {
                data: {
                    id: row.id,
                    type: type ?? 'others',
                },
            },
        });
    };

    return {
        contentList,
        initRows,
        researchProtocolRows,
        declarationRows,
        indemnityAgreementRows,
        insuranceCertificateRows,
        supportingDocumentRows,
        decisionDocumentRows,
        signedApplicationFormRows,
    };
};

export default useDocumentUploadData;
